import * as React from 'react'
import * as ReactDOM from 'react-dom'
import { ChevronLeft, ChevronRight, X } from 'lucide-react'
import { computeBars, rowIndexFromY, ROW_STEP, type TimelineBar } from './computeBars'
import type { GroupConfig, TimelineBarMapping } from './types'
import { GroupSectionHeader } from './components/GroupSectionHeader'
import * as s from './TimelineView.css'

const CHART_LEFT_OFFSET = 16
const CHART_RIGHT_OFFSET = 16
const TICK_COUNT = 6
/** Once the visible range spans more than this, axis ticks add a weekday label. */
const SHOW_DATE_THRESHOLD_MS = 36 * 60 * 60 * 1000
const CARD_GAP = 12
const UNGROUPED_KEY = '__timeline_ungrouped__'
/** A bar this short still counts as "active" for this long past its close, so a
 *  near-instant incident doesn't disappear from the hover card immediately. */
const NEAR_OPEN_WINDOW_MS = 15 * 60_000
/**
 * smart-web uses 15px here, tuned for its fixed 24h view (15px of a ~750px
 * track ≈ 29min). Product alerts render a 7-day range in the same pixel
 * budget, where 15px would be ≈3.4h — large enough that most real (minutes-
 * to-hours) incidents would render at this exact floor regardless of their
 * true duration. Kept smaller so bars stay visually proportional to duration
 * across a wider range, while still being a visible, hoverable sliver.
 */
const MIN_BAR_WIDTH_PX = 4
const LABEL_GAP_PX = 6
/** Fixed width of a product card's header (thumbnail/title) column in panel
 *  mode. Toggled to 0 when the panel is collapsed. */
const PANEL_HEADER_WIDTH_PX = 200
/** Fixed width of the alert context panel when open — keep in sync with
 *  TimelineView.css.ts's alertContextPanelInner width. Must be a concrete
 *  pixel value (not `undefined`/auto) on both sides of the open/closed
 *  toggle: CSS transitions cannot interpolate to/from `auto`, so animating
 *  between `0` and an unset width just snaps instantly instead of sliding. */
const ALERT_PANEL_WIDTH_PX = 260
/** Vertical gap between tracks within a card, and between cards — kept in
 *  sync with TimelineView.css.ts's productCardTracks `gap`. */
const CARD_TRACK_GAP_PX = 8
const LABEL_FONT_SIZE_PX = 11
const LABEL_FONT_WEIGHT = 600
const INSIDE_LABEL_PADDING_PX = 8
const LABEL_FONT_FAMILY = 'Roboto, Helvetica, Arial, sans-serif'

let measureCtx: CanvasRenderingContext2D | null | undefined
function measureLabelWidthPx(text: string): number {
  if (measureCtx === undefined) {
    measureCtx = typeof document !== 'undefined' ? document.createElement('canvas').getContext('2d') : null
  }
  if (!measureCtx) return text.length * (LABEL_FONT_SIZE_PX * 0.6)
  measureCtx.font = `${LABEL_FONT_WEIGHT} ${LABEL_FONT_SIZE_PX}px ${LABEL_FONT_FAMILY}`
  return measureCtx.measureText(text).width
}

export interface TimelineViewProps<TRow extends { id: string }> {
  rows: TRow[]
  loading?: boolean
  fillContainer?: boolean
  emptyTitle?: string
  emptySubtitle?: string
  mapping: TimelineBarMapping<TRow>
  /** ISO timestamps bounding the visible range. */
  rangeStart: string
  rangeEnd: string
  group?: GroupConfig<TRow>
  /**
   * Custom hover-card content for the rows active at the cursor's time.
   * Defaults to a plain dot + label list. Product alerts pass a render prop
   * here that renders the shared `AlertSummaryCard` — kept out of this
   * component so ui/ListView stays decoupled from app-level alert types.
   * `hoveredId` is the row whose bar the mouse is directly over (for a
   * matching in-list highlight); `selectedId` mirrors this component's own
   * `selectedId` prop (for a matching in-list "shown in panel" marker).
   */
  renderHoverCard?: (
    activeRows: TRow[],
    cursorTimeMs: number,
    hoveredId: string | null,
    selectedId: string | null,
  ) => React.ReactNode
  /** Controlled: the currently selected bar's row id, or null/undefined for none. */
  selectedId?: string | null
  /** Called with the clicked row when a bar is clicked, or null when the
   *  selection is cleared (clicking empty space, or the context panel's ✕). */
  onBarSelect?: (row: TRow | null) => void
  /** Renders the right-side "alert context" panel's content for the selected
   *  row. Panel only appears when both this and `selectedId` are set. */
  renderAlertContextPanel?: (row: TRow) => React.ReactNode
}

interface HoverState {
  pct: number
  timeMs: number
  /** Viewport coordinates, for the fixed-position portal card. */
  clientX: number
  clientY: number
  /** Container-relative, for ungrouped per-row highlight lookup. */
  containerY: number
}

function formatAxisTick(ms: number, spanMs: number): string {
  const d = new Date(ms)
  const time = d
    .toLocaleTimeString([], { hour: 'numeric', hour12: true })
    .replace(' ', '')
    .toLowerCase()
  if (spanMs <= SHOW_DATE_THRESHOLD_MS) return time
  return `${d.toLocaleDateString([], { weekday: 'short' })} ${time}`
}

function hexToRgba(hex: string, alpha: number): string {
  const m = /^#?([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(hex.trim())
  if (!m) return hex
  const [, r, g, b] = m
  return `rgba(${parseInt(r!, 16)}, ${parseInt(g!, 16)}, ${parseInt(b!, 16)}, ${alpha})`
}

interface RenderBar<TRow> {
  bar: TimelineBar
  row: TRow
}

/** One swimlane track per row key — shared between the grouped and ungrouped layouts. */
function TimelineTracks<TRow extends { id: string }>({
  rowKeys,
  barsByRowKey,
  mapping,
  rangeStartMs,
  rangeEndMs,
  nowMs,
  activeAtCursorIds,
  trackPx,
  selectedId,
  onBarClick,
  onBarHover,
}: {
  rowKeys: string[]
  barsByRowKey: Map<string, RenderBar<TRow>[]>
  mapping: TimelineBarMapping<TRow>
  rangeStartMs: number
  rangeEndMs: number
  nowMs: number
  activeAtCursorIds: Set<string>
  /** Actual rendered pixel width of the track (minus offsets) — used to decide
   *  whether a bar's label fits inside it, or must float beside it instead. */
  trackPx: number
  selectedId?: string | null
  onBarClick?: (row: TRow) => void
  /** Reports the specific bar the mouse is directly over (or null on leave) —
   *  used to highlight the matching line in the hover-card tooltip. */
  onBarHover?: (id: string | null) => void
}) {
  const showNowMarker = nowMs >= rangeStartMs && nowMs <= rangeEndMs
  const nowPct = ((nowMs - rangeStartMs) / (rangeEndMs - rangeStartMs)) * 100
  const offsetCalc = (pct: number) =>
    `calc(${CHART_LEFT_OFFSET}px + (100% - ${CHART_LEFT_OFFSET}px - ${CHART_RIGHT_OFFSET}px) * ${pct} / 100)`

  return (
    <>
      {rowKeys.map((rowKey) => (
        <div key={rowKey} className={s.rowTrack} style={{ paddingLeft: CHART_LEFT_OFFSET, paddingRight: CHART_RIGHT_OFFSET }}>
          <div className={s.trackBg} style={{ left: CHART_LEFT_OFFSET, right: CHART_RIGHT_OFFSET }} />
          {showNowMarker && <div className={s.nowMarker} style={{ left: offsetCalc(nowPct) }} />}
          {(barsByRowKey.get(rowKey) ?? []).map(({ bar, row }) => {
            const color = mapping.getColor(row)
            const isActive = activeAtCursorIds.has(bar.id)
            const isSelected = selectedId != null && row.id === selectedId
            // Three-level muting while a selection is active: selected (full
            // opacity, handled via barSelected's shadow below), secondary
            // (hovered/cursor-line-intersected — barSecondary), and everything
            // else (barDimmed). No selection active → no muting at all, same
            // as before 3-tier muting existed.
            const isSecondary = selectedId != null && !isSelected && isActive
            const isDimmed = selectedId != null && !isSelected && !isActive
            const label = mapping.getLabel(row)

            const barWidthPx = Math.max((bar.widthPct / 100) * trackPx, MIN_BAR_WIDTH_PX)
            const textWidthPx = measureLabelWidthPx(label.toUpperCase())
            const labelFitsInside = textWidthPx + INSIDE_LABEL_PADDING_PX * 2 <= barWidthPx
            const roomLeftPct = bar.leftPct
            const roomRightPct = 100 - (bar.leftPct + bar.widthPct)
            const growLeftward = roomLeftPct > roomRightPct
            // Cap the floated label to the room actually available within the
            // track, so it clips with an ellipsis instead of overflowing past
            // the track's bounds.
            const roomLeftPx = Math.max((roomLeftPct / 100) * trackPx - LABEL_GAP_PX, 0)
            const roomRightPx = Math.max((roomRightPct / 100) * trackPx - LABEL_GAP_PX, 0)

            const labelPositionStyle: React.CSSProperties = labelFitsInside
              ? {
                  left: offsetCalc(bar.leftPct),
                  width: `calc((100% - ${CHART_LEFT_OFFSET}px - ${CHART_RIGHT_OFFSET}px) * ${bar.widthPct} / 100)`,
                  minWidth: MIN_BAR_WIDTH_PX,
                  justifyContent: 'center',
                }
              : growLeftward
                ? {
                    left: `calc(${offsetCalc(bar.leftPct)} - ${LABEL_GAP_PX}px)`,
                    transform: 'translateX(-100%)',
                    maxWidth: roomLeftPx,
                  }
                : {
                    left: `calc(max(${offsetCalc(bar.leftPct + bar.widthPct)}, ${offsetCalc(bar.leftPct)} + ${MIN_BAR_WIDTH_PX}px) + ${LABEL_GAP_PX}px)`,
                    maxWidth: roomRightPx,
                  }

            return (
              <React.Fragment key={bar.id}>
                <div
                  className={[
                    s.bar,
                    bar.isOpen ? s.barStripe : null,
                    isSelected ? s.barSelected : null,
                    isSecondary ? s.barSecondary : null,
                    isDimmed ? s.barDimmed : null,
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  onClick={onBarClick ? (e) => { e.stopPropagation(); onBarClick(row) } : undefined}
                  onMouseEnter={onBarHover ? () => onBarHover(bar.id) : undefined}
                  onMouseLeave={onBarHover ? () => onBarHover(null) : undefined}
                  data-bar-id={bar.id}
                  style={{
                    left: offsetCalc(bar.leftPct),
                    width: `calc((100% - ${CHART_LEFT_OFFSET}px - ${CHART_RIGHT_OFFSET}px) * ${bar.widthPct} / 100)`,
                    borderTopLeftRadius: bar.clipsLeft ? 0 : 4,
                    borderBottomLeftRadius: bar.clipsLeft ? 0 : 4,
                    borderTopRightRadius: bar.clipsRight || bar.isOpen ? 0 : 4,
                    borderBottomRightRadius: bar.clipsRight || bar.isOpen ? 0 : 4,
                    // backgroundColor (longhand), not `background` — the shorthand
                    // implicitly resets background-size to its initial value (auto),
                    // which (being inline) beats the barStripe class's explicit
                    // `background-size: 12px 12px` regardless of source order. That
                    // silently broke the stripe tiling: instead of repeating at a
                    // fixed 12px, the gradient stretched to fill the whole bar,
                    // rescaling (and visibly seaming) as the bar's width changed.
                    backgroundColor: hexToRgba(color, bar.isOpen ? 0.05 : 0.12),
                    border: `1px ${bar.isOpen ? 'dashed' : 'solid'} ${color}`,
                    backgroundImage: bar.isOpen
                      ? `linear-gradient(45deg, ${hexToRgba(color, 0.25)} 25%, transparent 25%, transparent 50%, ${hexToRgba(color, 0.25)} 50%, ${hexToRgba(color, 0.25)} 75%, transparent 75%, transparent)`
                      : undefined,
                    boxShadow: isSelected
                      ? `0 0 0 3px ${color}, 0 0 8px 2px ${hexToRgba(color, 0.5)}`
                      : isActive
                        ? `0 0 0 2px ${color}`
                        : undefined,
                  }}
                />
                <span
                  className={[
                    s.barLabel,
                    isSecondary ? s.barSecondary : null,
                    isDimmed ? s.barDimmed : null,
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  style={{ color, ...labelPositionStyle }}
                >
                  {label}
                </span>
              </React.Fragment>
            )
          })}
        </div>
      ))}
    </>
  )
}

export function TimelineView<TRow extends { id: string }>({
  rows,
  loading = false,
  fillContainer: _fillContainer = false,
  emptyTitle = 'No data',
  emptySubtitle,
  mapping,
  rangeStart,
  rangeEnd,
  group,
  renderHoverCard,
  selectedId,
  onBarSelect,
  renderAlertContextPanel,
}: TimelineViewProps<TRow>) {
  const containerRef = React.useRef<HTMLDivElement>(null)
  const cardRef = React.useRef<HTMLDivElement>(null)
  const [hover, setHover] = React.useState<HoverState | null>(null)
  const [hoveredBarId, setHoveredBarId] = React.useState<string | null>(null)
  const [collapsedGroups, setCollapsedGroups] = React.useState<Set<string>>(() => new Set())
  const [containerWidthPx, setContainerWidthPx] = React.useState(600)
  const [groupPanelOpen, setGroupPanelOpen] = React.useState(true)
  const [tailTopPx, setTailTopPx] = React.useState<number | null>(null)
  const isGroupPanelMode = group?.display === 'panel'
  /** Width consumed by each card's header column — 0 when not in panel mode
   *  or when the panel is collapsed. Tracks/crosshair/axis math below all
   *  subtract this from the full container width they'd otherwise assume. */
  const headerOffsetPx = isGroupPanelMode && groupPanelOpen ? PANEL_HEADER_WIDTH_PX : 0

  React.useEffect(() => {
    const container = containerRef.current
    if (!container) return
    const observer = new ResizeObserver((entries) => {
      const width = entries[0]?.contentRect.width
      if (width != null) setContainerWidthPx(width)
    })
    observer.observe(container)
    return () => observer.disconnect()
  }, [rows.length > 0])

  const trackPx = Math.max(containerWidthPx - headerOffsetPx - CHART_LEFT_OFFSET - CHART_RIGHT_OFFSET, 1)

  const rangeStartMs = React.useMemo(() => Date.parse(rangeStart), [rangeStart])
  const rangeEndMs = React.useMemo(() => Date.parse(rangeEnd), [rangeEnd])
  const spanMs = rangeEndMs - rangeStartMs

  const rowsById = React.useMemo(() => new Map(rows.map((r) => [r.id, r])), [rows])

  // A filter change (e.g. the date range) refetches `rows` out from under a
  // stale `selectedId` — the context panel already handles this correctly
  // (selectedRow below falls back to null), but the bar-dimming logic in
  // TimelineTracks only keys off "is selectedId set at all", so every bar
  // was staying muted forever since nothing could ever match the no-longer-
  // present id. Tell the caller to actually clear its selection state too.
  React.useEffect(() => {
    if (selectedId != null && !rowsById.has(selectedId)) {
      onBarSelect?.(null)
    }
  }, [selectedId, rowsById, onBarSelect])

  const allBars = React.useMemo(() => {
    if (!Number.isFinite(rangeStartMs) || !Number.isFinite(rangeEndMs)) return []
    const inputs = rows.map((row) => {
      const endIso = mapping.getEnd(row)
      return {
        id: row.id,
        // Default to one swimlane per bar (row.id) — never bucket unrelated bars
        // into a shared row unless the caller explicitly opts in via getRowKey,
        // since bars sharing a row are drawn on top of each other when their
        // time ranges overlap.
        rowKey: mapping.getRowKey?.(row) ?? row.id,
        startMs: Date.parse(mapping.getStart(row)),
        endMs: endIso ? Date.parse(endIso) : null,
      }
    })
    return computeBars(inputs, rangeStartMs, rangeEndMs)
  }, [rows, mapping, rangeStartMs, rangeEndMs])

  const barsById = React.useMemo(() => new Map(allBars.map((b) => [b.id, b])), [allBars])

  // ── Bucket rows into groups (or a single ungrouped bucket) ──────────────
  const groupedRowIds = React.useMemo(() => {
    if (!group) return new Map([[UNGROUPED_KEY, rows.map((r) => r.id)]])
    const buckets = new Map<string, string[]>()
    for (const row of rows) {
      const key = group.getGroup(row)
      if (!buckets.has(key)) buckets.set(key, [])
      buckets.get(key)!.push(row.id)
    }
    if (!group.groupOrder) return buckets
    const ordered = new Map<string, string[]>()
    for (const key of group.groupOrder) {
      if (buckets.has(key)) ordered.set(key, buckets.get(key)!)
    }
    for (const [key, ids] of buckets) {
      if (!ordered.has(key)) ordered.set(key, ids)
    }
    return ordered
  }, [rows, group])

  // Per-group render data, shared between the chart's track blocks and (in
  // panel mode) the left group panel's cards — computed once so both stay in
  // lockstep without duplicating the bucketing/sort logic.
  const groupSections = React.useMemo(() => {
    return [...groupedRowIds.entries()].map(([groupKey, ids]) => {
      const groupBars: RenderBar<TRow>[] = ids
        .map((id) => {
          const bar = barsById.get(id)
          const row = rowsById.get(id)
          return bar && row ? { bar, row } : null
        })
        .filter((v): v is RenderBar<TRow> => v != null)
        // Chronological top-to-bottom — also determines row order below,
        // since each row key is first seen in this sorted pass.
        .sort((a, b) => a.bar.openedMs - b.bar.openedMs)

      const barsByRowKey = new Map<string, RenderBar<TRow>[]>()
      for (const rb of groupBars) {
        if (!barsByRowKey.has(rb.bar.rowKey)) barsByRowKey.set(rb.bar.rowKey, [])
        barsByRowKey.get(rb.bar.rowKey)!.push(rb)
      }
      const rowKeys = [...barsByRowKey.keys()]
      return { groupKey, groupBars, barsByRowKey, rowKeys }
    })
  }, [groupedRowIds, barsById, rowsById])

  // ── Time-cursor tracking (shared across all group sections) ─────────────
  const handleMouseMove = (event: React.MouseEvent<HTMLDivElement>) => {
    const container = containerRef.current
    if (!container || spanMs <= 0) return
    const rect = container.getBoundingClientRect()
    const trackStart = rect.left + headerOffsetPx + CHART_LEFT_OFFSET
    const trackEnd = rect.right - CHART_RIGHT_OFFSET
    if (trackEnd <= trackStart) return
    // In panel mode, rowsContainer spans the group header column too (so the
    // header compresses in lockstep with the tracks) — but the crosshair and
    // hover tooltip should only appear over the tracks themselves, not while
    // the cursor is over a product's header/thumbnail column.
    if (event.clientX < rect.left + headerOffsetPx) {
      setHover(null)
      setHoveredBarId(null)
      return
    }
    const clientX = Math.min(Math.max(event.clientX, trackStart), trackEnd)
    const pct = ((clientX - trackStart) / (trackEnd - trackStart)) * 100
    setHover({
      pct,
      timeMs: rangeStartMs + (pct / 100) * spanMs,
      clientX: event.clientX,
      clientY: event.clientY,
      containerY: Math.min(Math.max(event.clientY - rect.top, 0), rect.height),
    })
  }
  const handleMouseLeave = () => {
    setHover(null)
    setHoveredBarId(null)
  }

  const activeAtCursor = React.useMemo(() => {
    if (!hover) return []
    return allBars.filter(
      (b) => hover.timeMs >= b.openedMs && hover.timeMs <= Math.max(b.closedMs, b.openedMs + NEAR_OPEN_WINDOW_MS),
    )
  }, [hover, allBars])

  // Ungrouped mode can narrow the outline to just the row under the cursor's Y;
  // grouped mode (variable header heights between sections) outlines every
  // active-at-cursor bar instead — still correct, just less pixel-precise.
  const activeAtCursorIds = React.useMemo(() => {
    if (!hover) return new Set<string>()
    if (group) return new Set(activeAtCursor.map((b) => b.id))
    const rowKeysInOrder = [...new Set(allBars.map((b) => b.rowKey))]
    const hoveredRowKey = rowKeysInOrder[rowIndexFromY(hover.containerY)]
    return new Set(
      activeAtCursor.filter((b) => b.rowKey === hoveredRowKey).map((b) => b.id),
    )
  }, [hover, activeAtCursor, allBars, group])

  // Vertical center (relative to containerRef, which shares its top edge with
  // both chartColumn and the alert context panel — see the speech-bubble tail
  // render below) of the selected bar, so the tail can point at its row.
  // Re-measured via getBoundingClientRect rather than offsetTop: the bar's
  // nearest positioned ancestor is its own rowTrack, not containerRef, so
  // offsetTop alone wouldn't be relative to the right element.
  React.useLayoutEffect(() => {
    const container = containerRef.current
    if (!selectedId || !container) {
      setTailTopPx(null)
      return
    }
    const el = container.querySelector<HTMLElement>(`[data-bar-id="${CSS.escape(selectedId)}"]`)
    if (!el) {
      setTailTopPx(null)
      return
    }
    const containerRect = container.getBoundingClientRect()
    const elRect = el.getBoundingClientRect()
    setTailTopPx(elRect.top - containerRect.top + elRect.height / 2)
  }, [selectedId, groupSections, containerWidthPx])

  const now = Date.now()

  if (loading) {
    return (
      <div className={s.root}>
        <div className={s.rowsContainer}>
          {[0, 1].map((i) => (
            <div key={i} className={s.skeletonRow} />
          ))}
        </div>
      </div>
    )
  }

  if (rows.length === 0) {
    return (
      <div className={s.root}>
        <div className={s.emptyRow}>{emptySubtitle ? `${emptyTitle}. ${emptySubtitle}` : emptyTitle}</div>
      </div>
    )
  }

  const cardWidth = cardRef.current?.offsetWidth ?? 220
  const cardHeight = cardRef.current?.offsetHeight ?? 60
  const cardLeft = hover
    ? hover.clientX + CARD_GAP + cardWidth > window.innerWidth
      ? Math.max(hover.clientX - CARD_GAP - cardWidth, 4)
      : hover.clientX + CARD_GAP
    : 0
  const cardTop = hover
    ? hover.clientY - cardHeight - CARD_GAP < 0
      ? Math.min(hover.clientY + CARD_GAP, Math.max(window.innerHeight - cardHeight, 0))
      : hover.clientY - cardHeight - CARD_GAP
    : 0

  // Used for axis ticks (inside axisTickArea, already narrowed to exclude the
  // header column — see the axis row's own header spacer below).
  const offsetCalc = (pct: number) =>
    `calc(${CHART_LEFT_OFFSET}px + (100% - ${CHART_LEFT_OFFSET}px - ${CHART_RIGHT_OFFSET}px) * ${pct} / 100)`

  // Used for the crosshair, which lives inside rowsContainer (the FULL-width
  // container spanning the header column too, in panel mode) — so unlike
  // offsetCalc above, this one must itself subtract headerOffsetPx.
  const crosshairOffsetCalc = (pct: number) =>
    `calc(${headerOffsetPx + CHART_LEFT_OFFSET}px + (100% - ${headerOffsetPx + CHART_LEFT_OFFSET + CHART_RIGHT_OFFSET}px) * ${pct} / 100)`

  const selectedRow = selectedId != null ? rowsById.get(selectedId) ?? null : null

  return (
    <div className={s.root}>
      <div className={s.timelineLayout}>
        {isGroupPanelMode && (
          <button
            type="button"
            className={s.groupPanelToggle}
            onClick={() => setGroupPanelOpen((v) => !v)}
            aria-label={groupPanelOpen ? 'Collapse product panels' : 'Expand product panels'}
            aria-expanded={groupPanelOpen}
          >
            {groupPanelOpen ? <ChevronLeft size={12} /> : <ChevronRight size={12} />}
          </button>
        )}

        <div className={s.chartColumn}>
          <div
            ref={containerRef}
            className={s.rowsContainer}
            style={isGroupPanelMode ? { gap: CARD_TRACK_GAP_PX } : undefined}
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
            onClick={() => onBarSelect?.(null)}
          >
            {groupSections.map(({ groupKey, groupBars, barsByRowKey, rowKeys }) => {
              const tracks = (
                <TimelineTracks
                  rowKeys={rowKeys}
                  barsByRowKey={barsByRowKey}
                  mapping={mapping}
                  rangeStartMs={rangeStartMs}
                  rangeEndMs={rangeEndMs}
                  nowMs={now}
                  activeAtCursorIds={activeAtCursorIds}
                  trackPx={trackPx}
                  selectedId={selectedId}
                  onBarClick={onBarSelect}
                  onBarHover={setHoveredBarId}
                />
              )

              if (!group) return <React.Fragment key={groupKey}>{tracks}</React.Fragment>

              if (isGroupPanelMode) {
                // Header + tracks live inside one bordered card, so the whole
                // unit (not just the tracks) compresses together when the
                // right-side context panel opens.
                return (
                  <div key={groupKey} className={s.productCard}>
                    <div className={s.productCardHeader} style={{ width: groupPanelOpen ? PANEL_HEADER_WIDTH_PX : 0 }}>
                      {group!.renderGroupLabel ? group!.renderGroupLabel(groupKey) : groupKey}
                    </div>
                    <div className={s.productCardTracks}>{tracks}</div>
                  </div>
                )
              }

              const collapsed = collapsedGroups.has(groupKey)
              return (
                <section key={groupKey} className={s.groupSection}>
                  <div className={s.groupSectionHeaderWrap}>
                    <GroupSectionHeader
                      label={group.renderGroupLabel ? group.renderGroupLabel(groupKey) : groupKey}
                      count={groupBars.length}
                      collapsed={collapsed}
                      onToggle={() =>
                        setCollapsedGroups((prev) => {
                          const next = new Set(prev)
                          if (next.has(groupKey)) next.delete(groupKey)
                          else next.add(groupKey)
                          return next
                        })
                      }
                    />
                  </div>
                  {!collapsed && tracks}
                </section>
              )
            })}

            {hover && <div className={s.crosshairLine} style={{ left: crosshairOffsetCalc(hover.pct) }} />}
          </div>

          <div className={s.axisRow}>
            {isGroupPanelMode && <div className={s.axisHeaderSpacer} style={{ width: headerOffsetPx }} />}
            <div className={s.axisTickArea}>
              {Array.from({ length: TICK_COUNT }, (_, i) => {
                const pct = (i / (TICK_COUNT - 1)) * 100
                const ms = rangeStartMs + spanMs * (i / (TICK_COUNT - 1))
                const isFirst = i === 0
                const isLast = i === TICK_COUNT - 1
                return (
                  <span
                    key={i}
                    className={s.axisLabel}
                    style={{
                      left: offsetCalc(pct),
                      transform: isFirst ? undefined : isLast ? 'translateX(-100%)' : 'translateX(-50%)',
                    }}
                  >
                    {formatAxisTick(ms, spanMs)}
                  </span>
                )
              })}
            </div>
          </div>
        </div>

        {renderAlertContextPanel && (
          <div className={s.alertContextPanel} style={{ width: selectedRow ? ALERT_PANEL_WIDTH_PX : 0 }}>
            <div
              className={[s.alertContextPanelBg, selectedRow ? s.alertContextPanelBgVisible : null]
                .filter(Boolean)
                .join(' ')}
            />
            <div className={s.alertContextPanelInner}>
              {selectedRow && (
                <>
                  <button
                    type="button"
                    className={s.alertContextPanelClose}
                    onClick={() => onBarSelect?.(null)}
                    aria-label="Close"
                  >
                    <X size={14} />
                  </button>
                  {renderAlertContextPanel(selectedRow)}
                </>
              )}
            </div>
          </div>
        )}

        {renderAlertContextPanel && (
          <div
            className={[s.contextRightEdgeAnchor, selectedRow ? s.contextRightEdgeAnchorVisible : null]
              .filter(Boolean)
              .join(' ')}
          />
        )}

        {renderAlertContextPanel && selectedRow && tailTopPx != null && (
          <>
            <div className={s.contextTailOutline} style={{ right: ALERT_PANEL_WIDTH_PX, top: tailTopPx }} />
            <div className={s.contextTailFill} style={{ right: ALERT_PANEL_WIDTH_PX, top: tailTopPx }} />
          </>
        )}
      </div>

      {hover && activeAtCursor.length > 0 && typeof document !== 'undefined'
        ? ReactDOM.createPortal(
            <div className={s.hoverCardPortal} style={{ left: cardLeft, top: cardTop }}>
              <div ref={cardRef} className={s.hoverCardInner}>
                <span className={s.hoverCardTime}>
                  {new Date(hover.timeMs).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
                </span>
                {renderHoverCard
                  ? renderHoverCard(
                      activeAtCursor.map((b) => rowsById.get(b.id)).filter((r): r is TRow => r != null),
                      hover.timeMs,
                      hoveredBarId,
                      selectedId ?? null,
                    )
                  : activeAtCursor.map((b) => {
                      const row = rowsById.get(b.id)
                      if (!row) return null
                      const color = mapping.getColor(row)
                      return (
                        <div key={b.id} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
                          <span style={{ width: 8, height: 8, borderRadius: '50%', background: color, flexShrink: 0 }} />
                          <span>{mapping.getLabel(row)}</span>
                        </div>
                      )
                    })}
              </div>
            </div>,
            document.body,
          )
        : null}
    </div>
  )
}
